import { ComponentProps, ReactNode, forwardRef } from 'react';
import { Button, ButtonProps, buttonVariants } from 'v5/design-system/Button';
import { ButtonGroup } from 'v5/design-system/ButtonGroup';
import { cn } from 'v5/platform/dom/cn';
import { X } from 'lucide-react';

export interface AbstractFilterWidgetInternalProps
  extends ComponentProps<'div'> {
  onRemove: () => void;
  filterName: ReactNode;
  filterIcon: ReactNode;
  operatorSlot: ReactNode;
  valueSlot: ReactNode;
  filterField?: string;
}

/**
 * Use this when you implement your specific filter widget, like EnumFilterWidget.
 */
export type FilterWidgetPropsBase<T> = Omit<
  AbstractFilterWidgetInternalProps,
  'operatorSlot' | 'valueSlot'
> & {
  value: T;
  onValueChange: (value: T) => void;
};

export interface FilterWidgetOperatorTriggerProps extends ButtonProps {
  'data-element'?: string;
}

export interface FilterWidgetValueTriggerProps extends ButtonProps {
  'data-element'?: string;
}

// Use forwardRef for proper ref forwarding.
export const AbstractFilterWidget = forwardRef<
  HTMLDivElement,
  AbstractFilterWidgetInternalProps
>(function AbstractFilterWidget(
  {
    onRemove,
    filterName,
    filterIcon,
    operatorSlot,
    valueSlot,
    filterField,
    ...rest
  },
  ref
) {
  return (
    <ButtonGroup
      orientation="horizontal"
      ref={ref}
      data-filter-widget-field={filterField}
      {...rest}
      onKeyDown={e => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          onRemove();
        }
      }}
    >
      <div
        className={cn(
          buttonVariants({ variant: 'secondaryMuted', size: 'sm' })
        )}
      >
        {filterIcon ? <span>{filterIcon}</span> : null}
        {filterName ? <span>{filterName}</span> : null}
      </div>
      {operatorSlot}
      {valueSlot}
      <Button
        variant="secondary"
        size="sm"
        className={cn(
          buttonVariants({
            variant: 'secondaryMuted',
            size: 'sm',
            layout: 'icon'
          })
        )}
        aria-label="Remove filter"
        onClick={onRemove}
        data-element="delete-trigger"
      >
        <X />
      </Button>
    </ButtonGroup>
  );
});

export const AbstractFilterWidgetOperatorTrigger = forwardRef<
  HTMLButtonElement,
  FilterWidgetOperatorTriggerProps
>(function AbstractFilterWidgetOperatorTrigger({ ...rest }, ref) {
  return <Button ref={ref} variant="secondary" size="sm" {...rest} />;
});

export const AbstractFilterWidgetValueTrigger = forwardRef<
  HTMLButtonElement,
  FilterWidgetValueTriggerProps
>(function AbstractFilterWidgetValueTrigger({ className, ...rest }, ref) {
  return (
    <Button
      ref={ref}
      variant="secondary"
      size="sm"
      className={cn('justify-start px-1.5 gap-1.5', className)}
      {...rest}
    />
  );
});
